"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SETTING_KEYS = exports.SETTINGS_REGISTRY = void 0;
exports.isSettingKey = isSettingKey;
exports.parseSettingValue = parseSettingValue;
const zod_1 = require("zod");
function define(definition) {
    return definition;
}
/**
 * Single source of truth for runtime settings (spec 3.1). The admin UI renders forms from this,
 * the API validates against it, SettingsService reads through it. Adding a key here is the whole
 * change needed to expose a new tunable.
 */
exports.SETTINGS_REGISTRY = {
    'otp.ttl_seconds': define({
        group: 'otp',
        label: 'Code lifetime',
        description: 'How long a one-time code stays valid after it is sent.',
        unit: 'seconds',
        schema: zod_1.z.number().int().min(60).max(3600),
        default: 600,
    }),
    'otp.max_attempts': define({
        group: 'otp',
        label: 'Wrong attempts allowed',
        description: 'After this many wrong codes the code is voided and a new one must be requested.',
        unit: 'attempts',
        schema: zod_1.z.number().int().min(1).max(10),
        default: 5,
    }),
    'otp.resend_cooldown_seconds': define({
        group: 'otp',
        label: 'Resend cooldown',
        description: 'Minimum time between two code emails for the same user.',
        unit: 'seconds',
        schema: zod_1.z.number().int().min(10).max(600),
        default: 60,
    }),
    'otp.max_sends_per_hour': define({
        group: 'otp',
        label: 'Codes per hour',
        description: 'Maximum number of code emails one user can request within an hour.',
        unit: 'emails',
        schema: zod_1.z.number().int().min(1).max(20),
        default: 5,
    }),
    'wallet.provisioning_retry_cooldown_seconds': define({
        group: 'wallet',
        label: 'Provisioning retry cooldown',
        description: 'How long a user must wait before retrying a failed wallet creation from the Mini App.',
        unit: 'seconds',
        schema: zod_1.z.number().int().min(0).max(3600),
        default: 600,
    }),
    'balances.sweep_interval_minutes': define({
        group: 'balances',
        label: 'Balance sweep interval',
        description: 'How often the worker re-reads every user balance from Fluxson to heal missed webhooks.',
        unit: 'minutes',
        schema: zod_1.z.number().int().min(15).max(1440),
        default: 360,
    }),
    'ops.telegram_chat_id': define({
        group: 'ops',
        label: 'Ops Telegram chat',
        description: 'Chat id that receives operational alerts from the bot. Empty disables alerts.',
        unit: null,
        schema: zod_1.z.string().min(1).nullable(),
        default: null,
    }),
    'withdrawals.enabled': define({
        group: 'withdrawals',
        label: 'Withdrawals enabled',
        description: 'Master switch. When off, the Mini App hides the button and the API refuses every new withdrawal. Withdrawals already in flight finish.',
        unit: null,
        schema: zod_1.z.boolean(),
        default: true,
    }),
    'withdrawals.monthly_limit_ethereum': define({
        group: 'withdrawals',
        label: 'Monthly withdrawals on Ethereum',
        description: 'How many withdrawals one user may make on Ethereum per calendar month (UTC). Staff can grant extra attempts to one user from the user card.',
        unit: 'withdrawals',
        schema: zod_1.z.number().int().min(0).max(1000),
        default: 10,
    }),
    'withdrawals.monthly_limit_tron': define({
        group: 'withdrawals',
        label: 'Monthly withdrawals on Tron',
        description: 'How many withdrawals one user may make on Tron per calendar month (UTC).',
        unit: 'withdrawals',
        schema: zod_1.z.number().int().min(0).max(1000),
        default: 10,
    }),
    'withdrawals.manual_approval_threshold': define({
        group: 'withdrawals',
        label: 'Manual approval from',
        description: 'Withdrawals of this amount or more wait for a staff decision instead of being approved automatically. Major units of the asset (USDT/USDC). 0 sends every withdrawal to review.',
        unit: 'USDT/USDC',
        schema: zod_1.z.string().regex(/^\d+(\.\d+)?$/),
        default: '1000',
    }),
    'withdrawals.daily_limit_major': define({
        group: 'withdrawals',
        label: 'Fluxson daily withdrawal cap',
        description: 'Forwarded to Fluxson as the tenant asset dailyWithdrawLimit, in major units. 0 is understood as "no cap" — see the withdrawals spec, section 10.',
        unit: 'USDT/USDC',
        schema: zod_1.z.string().regex(/^\d+(\.\d+)?$/),
        default: '0',
    }),
    'exchange.enabled': define({
        group: 'exchange',
        label: 'Exchange enabled',
        description: 'Master switch. When off, the Mini App hides the Exchange button and the API refuses every new order. Orders already paid keep being tracked.',
        unit: null,
        schema: zod_1.z.boolean(),
        default: true,
    }),
    'exchange.draft_ttl_minutes': define({
        group: 'exchange',
        label: 'Draft order lifetime',
        description: 'How long an order that has not been paid may stay open before it is marked expired on our side. The exchanger closes its own copy by time independently.',
        unit: 'minutes',
        schema: zod_1.z.number().int().min(5).max(240),
        default: 30,
    }),
    'registration.allowlist_enabled': define({
        group: 'registration',
        label: 'Invited registration only',
        description: 'When on, only an email present in the registration list can complete sign-up, and the account takes the ID from the list. When off, anyone can register and CCex generates the ID. Accounts that already exist are not affected.',
        unit: null,
        schema: zod_1.z.boolean(),
        default: false,
    }),
    'users.transfer_window_hours': define({
        group: 'users',
        label: 'Telegram transfer window',
        description: 'How long after staff press "Allow Telegram transfer" the user may register with the wallet email from another Telegram account.',
        unit: 'hours',
        schema: zod_1.z.number().int().min(1).max(168),
        default: 24,
    }),
    'pin.max_attempts': define({
        group: 'pin',
        label: 'Wrong attempts allowed',
        description: 'Wrong PIN entries in a row before the PIN is locked for a while.',
        unit: 'attempts',
        schema: zod_1.z.number().int().min(3).max(10),
        default: 5,
    }),
    'pin.lockout_base_seconds': define({
        group: 'pin',
        label: 'First lockout',
        description: 'How long the PIN stays locked after the first run of wrong entries; doubles each time.',
        unit: 'seconds',
        schema: zod_1.z.number().int().min(30).max(600),
        default: 60,
    }),
    'pin.lockout_max_seconds': define({
        group: 'pin',
        label: 'Longest lockout',
        description: 'The doubling stops here.',
        unit: 'seconds',
        schema: zod_1.z.number().int().min(600).max(86400),
        default: 3600,
    }),
    'pin.resume_lock_seconds': define({
        group: 'pin',
        label: 'Re-lock after background',
        description: 'How long the Mini App may stay in the background before it asks for the PIN again. 0 means always.',
        unit: 'seconds',
        schema: zod_1.z.number().int().min(0).max(3600),
        default: 60,
    }),
};
exports.SETTING_KEYS = Object.keys(exports.SETTINGS_REGISTRY);
function isSettingKey(key) {
    return Object.prototype.hasOwnProperty.call(exports.SETTINGS_REGISTRY, key);
}
function parseSettingValue(key, value) {
    return exports.SETTINGS_REGISTRY[key].schema.safeParse(value);
}
//# sourceMappingURL=registry.js.map